import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  List,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import BorderedSection from '../../../features/shared/borderedSection';
import { RelatedEntityConfigInput } from '../../../../types/generated-types';
import { defaultCompoundOperator } from '../helpers/constructors';
import ListItemButton from '@mui/material/ListItemButton';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { RelatedEntityFilterDialog } from './RelatedEntityFilterDialog';

const defaultRelatedEntityFilter = () => ({
  relationshipName: '',
  selectionCriteria: defaultCompoundOperator(),
  relatedEntities: [],
});

interface RelatedEntityFiltersBuilderProps {
  targetModel: string;
  relatedEntityFilters: RelatedEntityConfigInput[];
  onRelatedEntityFiltersChange: (
    updatedFilters: RelatedEntityConfigInput[],
  ) => void;
  tiers: number;
}

export const RelatedEntityFiltersBuilder: React.FC<
  RelatedEntityFiltersBuilderProps
> = ({
  targetModel,
  relatedEntityFilters,
  onRelatedEntityFiltersChange,
  tiers = 1,
}) => {
  const [isNew, setIsNew] = React.useState(false);
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);
  const [myFilters, setMyFilters] = React.useState<RelatedEntityConfigInput[]>(
    [],
  );

  useEffect(() => {
    setMyFilters(relatedEntityFilters ?? []);
  }, [relatedEntityFilters]);

  const [selectedRelatedEntityFilters, setSelectedRelatedEntityFilters] =
    useState<Set<string>>(new Set());

  const [relatedEntityFilter, setRelatedEntityFilter] =
    useState<RelatedEntityConfigInput>(defaultRelatedEntityFilter());

  const onEditRelatedEntityFilter = useCallback(
    (targetedRelatedEntityFilter: RelatedEntityConfigInput) => {
      setIsNew(false);
      setRelatedEntityFilter(targetedRelatedEntityFilter);
      setFilterDialogOpen(true);
    },
    [],
  );

  const onAddRelatedEntityFilter = useCallback(() => {
    setIsNew(true);
    setRelatedEntityFilter(defaultRelatedEntityFilter());
    setFilterDialogOpen(true);
  }, []);

  const onFilterDialogClose = useCallback(() => {
    setRelatedEntityFilter(defaultRelatedEntityFilter());
    setIsNew(true);
    setFilterDialogOpen(false);
  }, []);

  const onRemoveSelectedRelatedEntityFilters = useCallback(() => {
    const newFilters = relatedEntityFilters.filter(
      (filter) => !selectedRelatedEntityFilters.has(filter.relationshipName),
    );
    onRelatedEntityFiltersChange(newFilters);
    setSelectedRelatedEntityFilters(new Set<string>());
  }, [
    onRelatedEntityFiltersChange,
    relatedEntityFilters,
    selectedRelatedEntityFilters,
  ]);

  const onFilterUpdate = useCallback(
    (newOrUpdatedRelatedEntityFilter: RelatedEntityConfigInput) => {
      if (!isNew) {
        const newFilters = relatedEntityFilters.map((filter) =>
          filter.relationshipName ===
          newOrUpdatedRelatedEntityFilter.relationshipName
            ? newOrUpdatedRelatedEntityFilter
            : filter,
        );
        onRelatedEntityFiltersChange(newFilters);
      } else {
        onRelatedEntityFiltersChange([
          ...relatedEntityFilters,
          newOrUpdatedRelatedEntityFilter,
        ]);
      }
      setTimeout(() => {
        setFilterDialogOpen(false);
      }, 200);
    },
    [isNew, relatedEntityFilters, onRelatedEntityFiltersChange],
  );

  return (
    <>
      <BorderedSection title="Related Entity Filters">
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Button
              variant={'contained'}
              sx={{
                '.MuiButton-startIcon': {
                  marginRight: '0px !important',
                },
              }}
              onClick={onAddRelatedEntityFilter}
              startIcon={
                <AddIcon
                  fontSize="small"
                  sx={{
                    marginRight: 0,
                  }}
                />
              }
            >
              Filter
            </Button>
            {selectedRelatedEntityFilters.size > 0 && (
              <Button
                variant={'outlined'}
                sx={{
                  '.MuiButton-startIcon': {
                    marginRight: '0px !important',
                  },
                }}
                color="error"
                onClick={onRemoveSelectedRelatedEntityFilters}
                startIcon={<DeleteIcon />}
              >
                Remove Selected
              </Button>
            )}
          </Box>

          <Box sx={{ minWidth: 120 }}>
            {myFilters.length > 0 ? (
              <List sx={{ padding: '0px', margin: '0px', overflow: 'auto' }}>
                {myFilters.map((filter) => (
                  <ListItemButton
                    key={filter.relationshipName}
                    onClick={() => onEditRelatedEntityFilter(filter)}
                  >
                    <ListItemIcon>
                      <Checkbox
                        edge="start"
                        checked={selectedRelatedEntityFilters.has(
                          filter.relationshipName,
                        )}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (
                            selectedRelatedEntityFilters.has(
                              filter.relationshipName,
                            )
                          ) {
                            const x = new Set(
                              selectedRelatedEntityFilters.values(),
                            );
                            x.delete(filter.relationshipName);
                            setSelectedRelatedEntityFilters(x);
                          } else {
                            const x = new Set(
                              selectedRelatedEntityFilters.values(),
                            );
                            x.add(filter.relationshipName);
                            setSelectedRelatedEntityFilters(x);
                          }
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography
                          component="pre"
                          sx={{
                            fontFamily: 'inherit',
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            margin: 0,
                          }}
                        >
                          {filter.relationshipName}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            ) : (
              <div>No Related Entity Filters Defined</div>
            )}
          </Box>
        </>
      </BorderedSection>
      <RelatedEntityFilterDialog
        relatedEntityFilter={relatedEntityFilter}
        targetModel={targetModel}
        onRelatedEntityFilterChange={onFilterUpdate}
        tiers={tiers}
        open={filterDialogOpen}
        onClose={onFilterDialogClose}
      />
    </>
  );
};
